import React, { useState, useMemo } from 'react';
import { Card, Row, Col, ProgressBar, Table, Alert, Button, Spinner } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { CME_USER_API } from '../../../../utils/apiUrl';
import { Get, Post } from '../../../../utils/httpProvider';
import { toast } from 'react-toastify';

const CMEDashboard = () => {
    const queryClient = useQueryClient();
    const currentYear = new Date().getFullYear();
    const [expandedDetail, setExpandedDetail] = useState(null); // { catName: string, status: number }

    // 1. Lấy dữ liệu tóm tắt (dùng cho các box thống kê)
    const { isLoading: loadingSummary, isError: errorSummary, data: summaryData } = useQuery(
        ['cmeSummaryData'],
        () => Get(CME_USER_API.GET_MY_SUMMARY),
        { keepPreviousData: true }
    );

    // 2. Lấy danh sách hồ sơ chi tiết (để tìm các hồ sơ cần gia hạn)
    const { isLoading: loadingHistory, data: historyQueryResult } = useQuery(
        ['cmeUserHistory_Dashboard'],
        () => Post(CME_USER_API.GET_MY_HO_SO, null, { PageIndex: 1, PageSize: 1000 }).then(res => res.json())
    );

    // Mutation xử lý gia hạn
    const cloneMutation = useMutation(
        (id) => Post(`${CME_USER_API.POST_USER_CLONE}/${id}`),
        {
            onSuccess: () => {
                toast.success("Đã gia hạn hồ sơ thành công vào danh sách của bạn!");
                queryClient.invalidateQueries('cmeSummaryData');
                queryClient.invalidateQueries('cmeUserHistory_Dashboard');
                queryClient.invalidateQueries('cmeUserHistory');
            },
            onError: (err) => {
                toast.error("Lỗi khi gia hạn: " + (err.message || "Vui lòng thử lại sau."));
            }
        }
    );

    // Helper: Lấy giá trị thuộc tính không phân biệt hoa thường
    const gv = (obj, key) => {
        if (!obj) return undefined;
        return obj[key] !== undefined ? obj[key] : obj[key.charAt(0).toUpperCase() + key.slice(1)];
    };

    // Helper: Lấy năm từ chuỗi ngày tháng an toàn
    const getYearSafe = (dateVal) => {
        if (!dateVal) return 0;
        const d = new Date(dateVal);
        const year = d.getFullYear();
        if (!isNaN(year) && year > 1900) return year;
        if (typeof dateVal === 'string' && dateVal.includes('/')) {
            const parts = dateVal.split('/');
            const y = parseInt(parts[parts.length - 1]);
            if (!isNaN(y)) return y;
        }
        return 0;
    };

    const summaryItems = summaryData?.$values || summaryData?.data || summaryData || [];
    const historyItems = historyQueryResult?.Data?.$values || historyQueryResult?.Data || historyQueryResult?.data?.$values || historyQueryResult?.data || [];

    // Tối ưu hóa thống kê: Sử dụng dữ liệu tổng hợp từ Backend (Ground Truth)
    const stats = useMemo(() => {
        const yearSummaryItems = summaryItems.filter(x => gv(x, 'nam') === currentYear);

        const totalHoursYear = yearSummaryItems.reduce((sum, item) => sum + (gv(item, 'tongSoGio') || 0), 0);
        const totalRecordsYear = yearSummaryItems.reduce((sum, item) => sum + (gv(item, 'soHoSo') || 0), 0);
        const totalPendingRecordsYear = yearSummaryItems.reduce((sum, item) => sum + (gv(item, 'soHoSoChoDuyet') || 0), 0);
        const totalRejectedRecordsYear = yearSummaryItems.reduce((sum, item) => sum + (gv(item, 'soHoSoTuChoi') || 0), 0);

        const startYearCycle = Math.max(2026, currentYear - 4);
        const endYearCycle = startYearCycle + 4;
        const cycleItems = summaryItems.filter(x => gv(x, 'nam') >= startYearCycle && gv(x, 'nam') <= currentYear);
        const totalHoursCycle = cycleItems.reduce((sum, item) => sum + (gv(item, 'tongSoGio') || 0), 0);
        const progressPercent = Math.min(100, Math.round((totalHoursYear / 24) * 100));

        // TỐI ƯU: Tìm hồ sơ cần gia hạn (Renewal) sử dụng Index để tra cứu nhanh (O(N))
        const currentYearRenewalGocIds = new Set(
            historyItems
                .filter(item => gv(item, 'namTichLuy') === currentYear && !gv(item, 'isDeleted') && gv(item, 'hoSoGocId'))
                .map(item => gv(item, 'hoSoGocId').toString().toLowerCase())
        );

        const ongoingActivities = historyItems.filter(h => {
            const status = gv(h, 'trangThai');
            const namTichLuy = gv(h, 'namTichLuy');
            const isAnnualRenewal = gv(h, 'isAnnualRenewal');
            const denNgayYear = getYearSafe(gv(h, 'denNgay'));
            const hId = gv(h, 'id')?.toString().toLowerCase();

            // Chỉ xét hồ sơ GỐC đã DUYỆT từ năm cũ, có cấu hình gia hạn và còn thời hạn
            if (status !== 2 || namTichLuy >= currentYear || !isAnnualRenewal || denNgayYear < currentYear) return false;

            // Kiểm tra nhanh xem đã có bản gia hạn cho năm nay chưa
            return !currentYearRenewalGocIds.has(hId);
        });

        return {
            totalHoursYear,
            totalRecordsYear,
            totalPendingRecordsYear,
            totalRejectedRecordsYear,
            yearSummaryItems,
            totalHoursCycle,
            progressPercent,
            startYearCycle,
            endYearCycle,
            ongoingActivities
        };
    }, [summaryItems, historyItems, currentYear]);

    const {
        totalHoursYear,
        totalRecordsYear,
        totalPendingRecordsYear,
        totalRejectedRecordsYear,
        yearSummaryItems,
        totalHoursCycle,
        progressPercent,
        startYearCycle,
        endYearCycle,
        ongoingActivities
    } = stats;

    const cycleYears = useMemo(() => {
        const years = [];
        // Tạo danh sách 5 năm tính từ startYearCycle
        for (let i = 0; i < 5; i++) {
            years.push(startYearCycle + i);
        }
        // Sắp xếp giảm dần để năm mới nhất ở trên
        return years.sort((a, b) => b - a);
    }, [startYearCycle]);

    return (
        <div className="container-fluid py-3">
            <h4 className="mb-4">Bảng điều khiển CME - Năm {currentYear}</h4>

            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-white bg-info shadow border-0">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="text-uppercase mb-1 small opa-8">Tổng giờ đạt</h6>
                                    <h2 className="mb-0 font-weight-bold">{totalHoursYear} giờ</h2>
                                </div>
                                <div className="bg-white-20 p-2 rounded-circle">
                                    <i className="fas fa-clock fa-lg"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-white bg-success shadow border-0">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="text-uppercase mb-1 small opa-8">Đã duyệt</h6>
                                    <h2 className="mb-0 font-weight-bold">{totalRecordsYear} hồ sơ</h2>
                                </div>
                                <div className="bg-white-20 p-2 rounded-circle">
                                    <i className="fas fa-check-circle fa-lg"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-white bg-primary shadow border-0">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="text-uppercase mb-1 small opa-8">Đang chờ</h6>
                                    <h2 className="mb-0 font-weight-bold">{totalPendingRecordsYear} hồ sơ</h2>
                                </div>
                                <div className="bg-white-20 p-2 rounded-circle">
                                    <i className="fas fa-hourglass-half fa-lg"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-white bg-danger shadow border-0">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="text-uppercase mb-1 small opa-8">Từ chối</h6>
                                    <h2 className="mb-0 font-weight-bold">{totalRejectedRecordsYear} hồ sơ</h2>
                                </div>
                                <div className="bg-white-20 p-2 rounded-circle">
                                    <i className="fas fa-times-circle fa-lg"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col lg={8}>
                    <Card className="shadow-sm border-0 mb-4 h-100">
                        <Card.Header className="bg-white py-3 border-bottom-0">
                            <h6 className="mb-0 font-weight-bold text-dark">Chi tiết tích lũy định mức năm {currentYear}</h6>
                        </Card.Header>
                        <Card.Body className="pt-0">
                            {loadingSummary ? (
                                <div className="text-center py-5"><p>Đang tải dữ liệu...</p></div>
                            ) : errorSummary ? (
                                <Alert variant="danger">Không thể tải dữ liệu thống kê.</Alert>
                            ) : yearSummaryItems.length > 0 ? (
                                yearSummaryItems.map((item, idx) => {
                                    const hApproved = gv(item, 'soHoSo') || 0;
                                    const hPending = gv(item, 'soHoSoChoDuyet') || 0;
                                    const hRejected = gv(item, 'soHoSoTuChoi') || 0;
                                    const totalHours = gv(item, 'tongSoGio') || 0;
                                    const totalHoursPending = gv(item, 'tongSoGioChoDuyet') || 0;
                                    const target = 12; // Định mức danh mục 12h

                                    return (
                                        <div className="mb-4" key={idx}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <div className="font-weight-bold text-dark pr-3" style={{ fontSize: '0.9rem', lineHeight: '1.2' }}>
                                                    {item.tenDanhMuc}
                                                </div>
                                                <div className="text-info font-weight-bold" style={{ whiteSpace: 'nowrap' }}>{totalHours} giờ</div>
                                            </div>

                                            <div className="d-flex align-items-center small text-muted mb-2" style={{ gap: '15px' }}>
                                                <span
                                                    className={`cursor-pointer ${expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 2 ? 'font-weight-bold text-success' : ''}`}
                                                    onClick={() => setExpandedDetail(expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 2 ? null : { catName: item.tenDanhMuc, status: 2 })}
                                                >
                                                    <i className="fas fa-check text-success mr-1"></i> {hApproved} hồ sơ đã duyệt
                                                </span>
                                                <span
                                                    className={`cursor-pointer ${expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 1 ? 'font-weight-bold text-warning' : ''}`}
                                                    onClick={() => setExpandedDetail(expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 1 ? null : { catName: item.tenDanhMuc, status: 1 })}
                                                >
                                                    <i className="far fa-clock text-warning mr-1"></i> {hPending} đang chờ
                                                </span>
                                                <span
                                                    className={`cursor-pointer ${expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 3 ? 'font-weight-bold text-danger' : ''}`}
                                                    onClick={() => setExpandedDetail(expandedDetail?.catName === item.tenDanhMuc && expandedDetail?.status === 3 ? null : { catName: item.tenDanhMuc, status: 3 })}
                                                >
                                                    <i className="fas fa-times text-danger mr-1"></i> {hRejected} từ chối
                                                </span>
                                            </div>

                                            <ProgressBar style={{ height: '6px' }} className="shadow-none bg-light">
                                                <ProgressBar
                                                    variant="success"
                                                    now={Math.min(100, (totalHours / target) * 100)}
                                                    key={1}
                                                />
                                                <ProgressBar
                                                    variant="warning"
                                                    now={Math.min(100, (totalHoursPending / target) * 100)}
                                                    key={2}
                                                />
                                            </ProgressBar>

                                            {/* Drill-down Table */}
                                            {expandedDetail?.catName === item.tenDanhMuc && (
                                                <div className="mt-2 animate-fade-in bg-light p-2 rounded border">
                                                    <h6 className="small font-weight-bold mb-2 text-secondary px-1">
                                                        Danh sách hồ sơ {expandedDetail.status === 2 ? "đã duyệt" : expandedDetail.status === 1 ? "đang chờ" : "bị từ chối"}
                                                    </h6>
                                                    <div className="table-responsive">
                                                        <Table size="sm" bordered hover className="mb-0 bg-white">
                                                            <thead className="thead-light">
                                                                <tr style={{ fontSize: '0.75rem' }}>
                                                                    <th>Nội dung</th>
                                                                    <th className="text-center" style={{ width: '60px' }}>Số giờ</th>
                                                                    <th className="text-center" style={{ width: '90px' }}>Thời gian</th>
                                                                    {expandedDetail.status === 3 && <th>Lý do</th>}
                                                                </tr>
                                                            </thead>
                                                            <tbody style={{ fontSize: '0.75rem' }}>
                                                                {historyItems
                                                                    .filter(h => gv(h, 'tenDanhMuc') === item.tenDanhMuc && gv(h, 'namTichLuy') === currentYear && gv(h, 'trangThai') === expandedDetail.status)
                                                                    .map((h, i) => (
                                                                        <tr key={i}>
                                                                            <td className="text-truncate" style={{ maxWidth: '250px' }}>{gv(h, 'noiDung')}</td>
                                                                            <td className="text-center font-weight-bold text-info">{gv(h, 'soGioTinChi')}h</td>
                                                                            <td className="text-center text-muted">
                                                                                {new Date(gv(h, 'tuNgay')).toLocaleDateString('vi-VN')}
                                                                            </td>
                                                                            {expandedDetail.status === 3 && <td className="text-danger small">{gv(h, 'lyDoTuChoi') || '-'}</td>}
                                                                        </tr>
                                                                    ))
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center py-5">
                                    <i className="fas fa-folder-open fa-3x text-light mb-3"></i>
                                    <p className="text-muted">Chưa có hoạt động nào trong năm {currentYear}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="shadow-sm border-0 mb-4 bg-white">
                        <Card.Body className="text-center p-4">
                            <h6 className="font-weight-bold mb-3 small text-uppercase">Mục tiêu đào tạo năm {currentYear}</h6>
                            <div className="position-relative mb-4">
                                <h1 className="display-4 font-weight-bold text-primary mb-0">{totalHoursYear} <small className="h5 text-muted">/ 24</small></h1>
                                <p className="text-muted small mb-0">Giờ tín chỉ ĐÃ DUYỆT năm {currentYear}</p>
                            </div>
                            <ProgressBar
                                variant={progressPercent >= 100 ? "success" : "primary"}
                                now={progressPercent}
                                className="mb-2"
                                style={{ height: '10px', borderRadius: '5px' }}
                            />
                            <div className="d-flex justify-content-between small text-muted font-weight-bold">
                                <span>{progressPercent}% hoàn thành</span>
                                <span className="text-dark">Mục tiêu 24 giờ</span>
                            </div>
                        </Card.Body>
                    </Card>

                    {ongoingActivities.length > 0 && (
                        <Card className="shadow-sm border-0 mb-4 border-left-warning" style={{ borderLeft: '4px solid #ffc107' }}>
                            <Card.Header className="bg-white py-3 border-bottom-0 d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 font-weight-bold text-dark small text-uppercase"><i className="fas fa-sync-alt mr-2 text-warning"></i>Cần gia hạn định kỳ</h6>
                                <span className="badge badge-warning">{ongoingActivities.length}</span>
                            </Card.Header>
                            <Card.Body className="pt-0">
                                {ongoingActivities.map((h, i) => (
                                    <div key={i} className="p-2 mb-2 border rounded bg-light d-flex justify-content-between align-items-center">
                                        <div style={{ flex: 1, paddingRight: '10px', overflow: 'hidden' }}>
                                            <div className="font-weight-bold text-truncate small" title={gv(h, 'noiDung')}>{gv(h, 'noiDung')}</div>
                                            <div className="small text-muted" style={{ fontSize: '0.7rem' }}>Năm gốc: {gv(h, 'namTichLuy')}</div>
                                        </div>
                                        <Button
                                            variant="warning"
                                            size="sm"
                                            className="font-weight-bold py-1 px-2"
                                            style={{ fontSize: '0.65rem' }}
                                            onClick={() => cloneMutation.mutate(gv(h, 'id'))}
                                            disabled={cloneMutation.isLoading}
                                        >
                                            {cloneMutation.isLoading ? <Spinner animation="border" size="sm" /> : "Gia hạn"}
                                        </Button>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    )}

                    <Card className="shadow-sm border-0">
                        <Card.Body className="p-3">
                            <h6 className="font-weight-bold mb-3 small text-uppercase text-muted"><i className="fas fa-history mr-2"></i>TÓM TẮT CHU KỲ ({startYearCycle} - {endYearCycle})</h6>
                            <div className="d-flex justify-content-between align-items-end mb-1">
                                <div className="h4 mb-0 font-weight-bold text-dark">{totalHoursCycle} <small className="h6 text-muted">/ 120 giờ</small></div>
                                <div className="small font-weight-bold text-info">{Math.min(100, Math.round((totalHoursCycle / 120) * 100))}%</div>
                            </div>
                            <ProgressBar variant="info" now={Math.min(100, (totalHoursCycle / 120) * 100)} className="mb-4" style={{ height: '6px' }} />

                            {cycleYears.map(year => {
                                const yearTotal = summaryItems.filter(x => gv(x, 'nam') === year).reduce((sum, item) => sum + (gv(item, 'tongSoGio') || 0), 0);
                                const isCurrent = year === currentYear;
                                return (
                                    <div key={year} className={`d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom last-border-0 ${isCurrent ? 'text-primary' : ''}`}>
                                        <div className="font-weight-bold small">
                                            {isCurrent && <i className="fas fa-caret-right mr-1"></i>}
                                            Năm {year} {isCurrent && "(Hiện tại)"}
                                        </div>
                                        <div className={`badge ${isCurrent ? 'badge-primary' : 'badge-light'} p-2`} style={{ fontSize: '0.85rem' }}>{yearTotal} giờ</div>
                                    </div>
                                );
                            })}

                            <Alert variant="info" className="small mt-3 mb-0 border-0 p-2" style={{ fontSize: '0.75rem' }}>
                                <i className="fas fa-info-circle mr-1"></i> Quy định: Hoàn thành đủ 120 giờ trong chu kỳ xét duyệt 5 năm.
                            </Alert>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <style>{`
                .transition-all { transition: all 0.2s ease-in-out; }
                .bg-white-20 { background-color: rgba(255,255,255,0.2); }
                .opa-8 { opacity: 0.8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
                .last-border-0:last-child { border-bottom: 0 !important; }
                .cursor-pointer { cursor: pointer; }
                .italic { font-style: italic; }
            `}</style>
        </div>
    );
};

export default CMEDashboard;
